import Color from '@/Enums/Color.enum';
import { INITIAL_VIEW_STATE } from '@/constants/geo';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import GeoMapModal from '@/components/Map/GeoMapModal';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapGl, { Layer, Marker, Source } from 'react-map-gl';
import {
  createCircleByRadius,
  extractPointsFromLocations,
  filterPoints,
  getLocationsWithGeometry,
  mergeGeoData,
} from './helpers';
import { circleLayerStyle, exclusionsLayerStyle, locationLayerConfigs } from './mapStyle';

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const GeoMap = memo(
  ({
    locationsToShow = [],
    excludedLocations = [],
    reportNotFoundLocations,
    geoFence,
    geoImpressions = [],
    disableHover = false,
  }) => {
    const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
    const [locations, setLocations] = useState([]);
    const [exclusions, setExclusions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [radiusCircle, setRadiusCircle] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [modalCoordinates, setModalCoordinates] = useState(null);
    const [selectedZone, setSelectedZone] = useState(null);

    const missingBoundaryIdsRef = useRef(new Set());

    const rememberAndReportNotFound = useCallback(
      notFoundIds => {
        notFoundIds.forEach(id => missingBoundaryIdsRef.current.add(id));
        reportNotFoundLocations?.(notFoundIds);
      },
      [reportNotFoundLocations],
    );

    const impressionsIndex = useMemo(() => {
      const index = new Map();
      if (!Array.isArray(geoImpressions)) return index;
      for (const { type, apiName, impressions } of geoImpressions) {
        if (!index.has(type)) index.set(type, new Map());
        index.get(type).set(String(apiName), impressions);
      }
      return index;
    }, [geoImpressions]);

    const locationsLayers = useMemo(() => {
      return locationLayerConfigs.map(({ id, filter }) => (
        <Layer
          key={id}
          id={id}
          type='fill'
          source='locations'
          paint={{
            'fill-color': Color.Primary,
            'fill-opacity': 0.5,
          }}
          filter={filter}
        />
      ));
    }, []);

    const onMoveHandler = ({ viewState: vs }) => !isLoading && setViewState(vs);

    useEffect(() => {
      const resolvableLocations = locationsToShow.filter(({ id }) => !missingBoundaryIdsRef.current.has(id));
      getLocationsWithGeometry(resolvableLocations, locations, setLocations, setIsLoading, rememberAndReportNotFound);
    }, [locationsToShow, rememberAndReportNotFound]);

    useEffect(() => {
      const resolvableExclusions = excludedLocations.filter(({ id }) => !missingBoundaryIdsRef.current.has(id));
      getLocationsWithGeometry(resolvableExclusions, exclusions, setExclusions, undefined, notFoundIds =>
        notFoundIds.forEach(id => missingBoundaryIdsRef.current.add(id)),
      );
    }, [excludedLocations]);

    useEffect(() => {
      if (!geoFence) {
        setRadiusCircle(null);
        return;
      }

      const { longitude, latitude, radius } = geoFence;
      setRadiusCircle(createCircleByRadius([longitude, latitude], radius));
    }, [geoFence]);

    useEffect(() => {
      if (!locations.length && !radiusCircle) {
        setIsLoading(false);
        return;
      }

      const points = extractPointsFromLocations(locations, radiusCircle);

      const filteredPoints = points.filter(filterPoints);

      let bbox;
      let center;
      if (filteredPoints.length) {
        const filteredPointsCollection = turf.points(filteredPoints);

        bbox = turf.bbox(filteredPointsCollection);
        center = turf.center(filteredPointsCollection);
      } else {
        const pointsCollection = turf.points(points);

        bbox = turf.bbox(pointsCollection);
        center = turf.center(pointsCollection);
      }

      const deltaX = Math.abs(bbox[2] - bbox[0]) * 0.8;
      const deltaY = Math.abs(bbox[3] - bbox[1]) * 1.7;

      const zoom = Math.min(Math.log2(360 / deltaX), Math.log2(180 / deltaY));

      setViewState({
        latitude: center.geometry.coordinates[1],
        longitude: center.geometry.coordinates[0],
        zoom: zoom >= 0 ? zoom : 1,
      });
      setIsLoading(false);
    }, [locations, radiusCircle]);

    const onMouseMove = useCallback(
      ({ features, point }) => {
        const feature = features && features[0];
        if (!feature) {
          setShowModal(false);
          return;
        }

        const enriched = mergeGeoData(feature, impressionsIndex);
        if (!enriched) {
          setShowModal(false);
          return;
        }

        setModalCoordinates({
          x: `${point.x - 83}px`,
          y: `${point.y - 122}px`,
        });
        setSelectedZone(enriched);
        setShowModal(true);
      },
      [impressionsIndex],
    );

    const interactiveLayerIds = useMemo(() => locationLayerConfigs.map(cfg => cfg.id), []);

    return (
      <>
        {!disableHover && (
          <GeoMapModal
            show={showModal}
            modalCoordinates={modalCoordinates}
            selectedZone={selectedZone}
            impressionsOnly
          />
        )}
        <MapGl
          {...viewState}
          style={isLoading ? { filter: 'blur(1px)' } : {}}
          onMove={onMoveHandler}
          mapStyle={'mapbox://styles/mapbox/light-v9'}
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          interactiveLayerIds={interactiveLayerIds}
          preserveDrawingBuffer={true}
          onMouseMove={disableHover ? undefined : onMouseMove}
        >
          <Source
            id='locations'
            type='geojson'
            data={{
              type: 'FeatureCollection',
              features: locations,
            }}
          >
            {locationsLayers}
          </Source>

          <Source
            id='exclusions'
            type='geojson'
            data={{
              type: 'FeatureCollection',
              features: exclusions,
            }}
          >
            <Layer {...exclusionsLayerStyle} />
          </Source>

          {radiusCircle && geoFence && (
            <>
              <Source
                id='circle'
                type='geojson'
                data={{
                  type: 'FeatureCollection',
                  features: [radiusCircle],
                }}
              >
                <Layer {...circleLayerStyle} />
              </Source>
              <Marker latitude={geoFence.latitude} longitude={geoFence.longitude} />
            </>
          )}
        </MapGl>
      </>
    );
  },
);

GeoMap.propTypes = {
  locationsToShow: PropTypes.array,
  excludedLocations: PropTypes.array,
  reportNotFoundLocations: PropTypes.func,
  geoFence: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    radius: PropTypes.number,
  }),
  disableHover: PropTypes.bool,
};

export default GeoMap;
